<template>
  <div v-if="isStepVisible" class="ml-5 h-500 overflow-auto d-flex">
    <div v-if="importSuccess" class="flex-grow-1 d-flex flex-column justify-content-center text-center font-xl font-weight-semi-bold">
      <div>
        <CSpinner class="my-3" color="primary" />
      </div>
      <p class="text-center mb-4">
        {{ $t('audience-new.create-audience.success.text.1') }}<br />
        {{ $t('audience-new.create-audience.success.text.2') }}
        <b>{{ $t('audience-new.create-audience.success.text.3') }}</b>
      </p>
      <div>
        <CButton color="white" class="py-3 px-3" @click="redirectToHome">
          <router-link to="/" tag="span">{{ $t('audience.organize.complete.link.go-to') }}</router-link>
        </CButton>
      </div>
    </div>
    <div v-else>
      <p class="font-weight-semi-bold font-xl mb-4">{{ $t('audience-new.create-audience.step-intro') }}</p>
      <div class="d-flex align-items-start gap-10 mb-5">
        <div class="d-flex align-items-center min-w-100">
          <CSwitch :checked.sync="hasTag" :disabled="false" class="switch-custom" color="primary" shape="pill" size="lg" />
          <span class="ml-2 align-self-center">{{
            hasTag ? $t('audience-new.create-audience.switch.yes') : $t('audience-new.create-audience.switch.no')
          }}</span>
        </div>
        <div>
          <p class="font-lg mb-2 line-height-1">{{ $t('audience-new.create-audience.create-tag.question') }}</p>
          <div class="mt-1 d-flex align-items-baseline font-sm">
            <div>
              <CIcon name="infoCircle" size="custom-size" class="mr-2" :height="16" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
            </div>
            <p class="mb-0 max-w-633">{{ $t('audience-new.create-audience.create-tag.description') }}</p>
          </div>
        </div>
      </div>
      <div class="d-flex align-items-start gap-10">
        <div class="min-w-100">
          <CIcon name="peopleFull" size="custom-size" class="mr-2 text-primary" :height="90" viewBox="0 0 32 32" />
        </div>
        <div class="w-600">
          <div class="mt-1 d-flex align-items-baseline">
            <p class="mb-2">
              {{ $t('audience-new.create-audience.create-tag.search-create') }}
            </p>
            <div>
              <CIcon name="infoCircle" size="custom-size" class="ml-2" :height="16" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
            </div>
          </div>

          <multiselect
            v-model="tags"
            class="border-gray-300 min-w-340 tag-select-audience"
            :tag-placeholder="$t('audience-new.create-audience.create-tag.add-tag')"
            :options="tagsOptions"
            :multiple="true"
            :taggable="true"
            :show-labels="false"
            :disabled="!hasTag"
            @tag="addTag"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import services from '@/services.js'
import { IMPORT_CONTACTS_STEPS } from '../../../constants'

export default {
  name: 'CreateAudience',
  props: {
    /* Necessário para uso de v-if dentro de um v-show (elemento pai) */
    isStepVisible: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      hasTag: false,
      tagsOptions: [],
      tags: [],
      isLoadingStep: false,
      IMPORT_CONTACTS_STEPS,
      timeoutToRedirect: undefined,
    }
  },
  mounted() {
    this.tagsOptions = this.importedDataTags
  },
  computed: {
    organizationId() {
      return this.$route.params.organizationId
    },
    importSuccess() {
      return this.$store.state.audience.importContacts.importSuccess
    },
    importedDataTags() {
      return this.$store.state.audience.importContacts.importedData?.tags || []
    },
  },
  methods: {
    addTag(newTag) {
      this.tagsOptions = [...this.importedDataTags, newTag]
      this.tags.push(newTag)
      this.$store.dispatch('audience/addImportedDataTags', this.tagsOptions)
    },
    async sendForImport() {
      try {
        this.isLoadingStep = true
        const audienceStoreData = this.$store.state.audience.importContacts.importedData

        if (!audienceStoreData) throw new Error()

        const importSchemaTreated = audienceStoreData.items
          .filter((item) => item.state.value !== 'IGNORED')
          .map((element) => {
            delete element.edit
            delete element.lines
            delete element.messages
            delete element.new
            return element
          })

        const obj = {
          fileGuid: audienceStoreData.fileGuid,
          phoneCountryIndicator: audienceStoreData.phoneCountryIndicator,
          subscriptions: audienceStoreData.subscriptions,
          Tags: this.tags,
          ImportSchema: importSchemaTreated,
        }
        const response = await services.sendForImport(this.organizationId, obj)

        const isSucessfully = response.data.success

        this.$store.dispatch('audience/setImportSuccess', isSucessfully)

        if (isSucessfully) {
          const { completedSteps, currentStep } = this.$store.state.audience.importContacts
          this.$store.dispatch('audience/setImportContactsCompletedSteps', [...completedSteps, currentStep])
        }
      } catch (error) {
        const errorCode = error?.response?.data?.error?.code || error?.response?.data?.message || error?.response?.data?.messages[0]?.error?.code

        if (!errorCode) {
          this.$emit('addMessage', {
            status: 'danger',
            text: this.$t(`error.codes.DEFAULT`),
          })
          return
        }

        this.$emit('addMessage', {
          status: 'danger',
          text: this.$t(`general.errors.${errorCode}`),
        })
      } finally {
        this.isLoadingStep = false
      }
    },
    redirectToImportHistory() {
      this.timeoutToRedirect = setTimeout(() => {
        this.$router.push({ name: 'import-history' })
      }, 5000)
    },
    redirectToHome() {
      clearTimeout(this.timeoutToRedirect)
      this.$router.push({ name: 'home' })
    },
  },
  watch: {
    isLoadingStep(newValue) {
      this.$emit('changeLoadingStep', newValue)
    },
    importedDataTags(newValue) {
      this.tagsOptions = newValue

      if (this.$store.state.audience.importContacts.currentStep === IMPORT_CONTACTS_STEPS.CREATE_AUDIENCE) return

      this.tags = []
      this.hasTag = false
    },
    importSuccess(isImportSuccess) {
      if (!isImportSuccess) return

      this.redirectToImportHistory()
    },
  },
}
</script>

<style lang="scss" scoped>
.min-w-100 {
  min-width: 100px;
}
.min-w-340 {
  min-width: 340px;
}
.gap-10 {
  gap: 10px;
}
.line-height-1 {
  line-height: 1;
}
.w-600 {
  width: 600px;
}
.h-500 {
  height: 500px;
}
.overflow-auto {
  overflow: auto;
}
</style>

<style lang="scss">
.tag-select-audience .multiselect__tag {
  border-radius: 30px !important;
  background-color: var(--primary) !important;
  color: white;

  .multiselect__tag-icon::after {
    color: white;
  }
}
</style>
